/**
 * offlineQueueDB.ts — IndexedDB para a Offline Write Queue
 *
 * Banco separado do cache (bigwa_cache). Persiste operações de escrita
 * que não puderam ir para o Drive (offline ou erro de rede).
 * Sobrevive reload e fechamento de tab.
 *
 * Cap: MAX_QUEUE_SIZE operações para evitar uso excessivo de storage.
 */

import { nanoid } from 'nanoid';
import { logger } from '../../utils/logger';
import type { OfflineQueueEntry, OfflineQueueInput } from './types';
import { OfflineQueueFullError } from './types';

const DB_NAME = 'bigwa-offline-queue';
const DB_VERSION = 1;
const STORE_NAME = 'queue';
const MAX_QUEUE_SIZE = 500;

let db: IDBDatabase | null = null;
let initPromise: Promise<boolean> | null = null;

// ── Init ──────────────────────────────────────────────────────────────

/**
 * Abre (ou cria) o banco IndexedDB da fila offline.
 * Seguro para chamar múltiplas vezes — singleton via initPromise.
 */
export async function init(): Promise<boolean> {
  if (db) return true;
  if (initPromise) return initPromise;

  initPromise = new Promise<boolean>((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') {
        logger.warn('offlineQueueDB', 'IndexedDB não disponível neste ambiente');
        resolve(false);
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        logger.error('offlineQueueDB', 'Erro ao abrir banco:', request.error);
        resolve(false);
      };

      request.onsuccess = () => {
        db = request.result;

        // Se o banco for fechado inesperadamente, limpar referência
        db.onclose = () => {
          db = null;
          initPromise = null;
        };

        resolve(true);
      };

      request.onupgradeneeded = (event) => {
        const database = (event.target as IDBOpenDBRequest).result;

        if (!database.objectStoreNames.contains(STORE_NAME)) {
          const store = database.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('fileKey', 'fileKey', { unique: false });
          store.createIndex('status', 'status', { unique: false });
          store.createIndex('createdAt', 'createdAt', { unique: false });
        }
      };
    } catch (error) {
      logger.error('offlineQueueDB', 'Erro na inicialização:', error);
      resolve(false);
    }
  });

  return initPromise;
}

// ── Helpers ───────────────────────────────────────────────────────────

function getDB(): IDBDatabase {
  if (!db) throw new Error('offlineQueueDB não inicializado. Chame init() primeiro.');
  return db;
}

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ── CRUD ──────────────────────────────────────────────────────────────

/**
 * Adiciona operação à fila. Gera id e timestamp automaticamente.
 * @throws OfflineQueueFullError se a fila exceder MAX_QUEUE_SIZE
 */
export async function enqueue(input: OfflineQueueInput): Promise<string> {
  const database = getDB();

  // Verificar cap antes de adicionar
  const currentCount = await count();
  if (currentCount >= MAX_QUEUE_SIZE) {
    throw new OfflineQueueFullError(currentCount, MAX_QUEUE_SIZE);
  }

  const entry: OfflineQueueEntry = {
    ...input,
    id: nanoid(),
    createdAt: new Date().toISOString(),
    retries: 0,
    status: 'pending',
  };

  const tx = database.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  await promisify(store.add(entry));

  return entry.id;
}

/**
 * Remove uma operação da fila pelo id.
 */
export async function dequeue(id: string): Promise<void> {
  const database = getDB();
  const tx = database.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  await promisify(store.delete(id));
}

/**
 * Retorna todas as operações, ordenadas por createdAt.
 */
export async function getAll(): Promise<OfflineQueueEntry[]> {
  const database = getDB();
  const tx = database.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);
  const index = store.index('createdAt');
  const entries = await promisify(index.getAll());
  return entries;
}

/**
 * Retorna todas as operações para um fileKey específico.
 */
export async function getByFileKey(fileKey: string): Promise<OfflineQueueEntry[]> {
  const database = getDB();
  const tx = database.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);
  const index = store.index('fileKey');
  const entries = await promisify(index.getAll(fileKey));
  return entries;
}

/**
 * Retorna operações com um status específico.
 */
export async function getByStatus(status: OfflineQueueEntry['status']): Promise<OfflineQueueEntry[]> {
  const database = getDB();
  const tx = database.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);
  const index = store.index('status');
  const entries = await promisify(index.getAll(status));
  return entries;
}

/**
 * Atualiza o status e/ou retries de uma entrada.
 */
export async function updateEntry(
  id: string,
  updates: Partial<Pick<OfflineQueueEntry, 'status' | 'retries'>>
): Promise<void> {
  const database = getDB();
  const tx = database.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);

  const existing = await promisify(store.get(id));
  if (!existing) return;

  const updated = { ...existing, ...updates };
  await promisify(store.put(updated));
}

/**
 * Número total de operações na fila.
 */
export async function count(): Promise<number> {
  const database = getDB();
  const tx = database.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);
  return promisify(store.count());
}

/**
 * Limpa toda a fila.
 */
export async function clear(): Promise<void> {
  const database = getDB();
  const tx = database.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  await promisify(store.clear());
}

/**
 * Fecha o banco e limpa referências. Usado em logout.
 */
export function close(): void {
  if (db) {
    db.close();
    db = null;
    initPromise = null;
  }
}

/**
 * Deleta o banco inteiro. Usado em logout para limpeza completa.
 */
export async function deleteDatabase(): Promise<void> {
  close();
  return new Promise<void>((resolve, reject) => {
    try {
      const request = indexedDB.deleteDatabase(DB_NAME);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    } catch (error) {
      reject(error);
    }
  });
}

// Re-export constantes para testes
export { MAX_QUEUE_SIZE, DB_NAME };
